import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Context } from 'components/Store';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { parseServerError } from 'helpers/errorHelpers';
import { FullProduct } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { getRequestConfig } from 'screens/Applications&WorkOrders/apiHelpers';

export type OperationType = 'application' | 'work-order';

interface UseOperationProductsReturn {
  products: FullProduct[];
  isLoading: boolean;
}

const getEndpoint = (type: OperationType, id: string) => {
  const basePath = type === 'application' ? 'applications' : 'work-orders';
  return `${CROPWISE_PROXY_URL}/v1/base/${basePath}/${id}/products`;
};

const useOperationProducts = (
  operationId: string | null,
  operationType: OperationType
): UseOperationProductsReturn => {
  const [{ organization }, dispatch] = useContext(Context) as any;
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<FullProduct[]>([]);

  useEffect(() => {
    if (!operationId || !organization?.id) {
      setProducts([]);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const config = getRequestConfig(organization.id);
        config.signal = abortController.signal;

        const response = await axios.get<FullProduct[]>(
          getEndpoint(operationType, operationId),
          config
        );

        const filteredProducts = response.data.filter(
          product => !(product.isCarrier && product.isHidden)
        );
        setProducts(filteredProducts);
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();

    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [operationId, operationType, organization.id, dispatch]);

  return { products, isLoading };
};

export default useOperationProducts;
